import { parse } from 'node-html-parser';

// Keep these tag sets in sync with magellan-api/helpers/messaging/email-html-sanitizer.js.
// Tags that no email client renders and that spam filters flag as dangerous.
export const HTML_DISALLOWED_TAGS = ['iframe', 'script', 'object', 'embed'];

// Where each disallowed tag carries its payload: <iframe>/<embed> use src (and
// <iframe> can use srcdoc), while <object> uses data -- none of them overlap.
// Scoring any of these as content is what fires the "Removed embedded content"
// notification; missing one means the tag is still stripped but the embed
// silently vanishes with no notice (the failure this function exists to prevent).
const PAYLOAD_ATTRIBUTES = ['src', 'srcdoc', 'data'];

// Legacy Outlook/Dreamweaver exports (the main reason uppercase tags reach an
// email builder) write attributes uppercase, e.g. <IFRAME SRC="...">. node-html-parser
// preserves that source casing in rawAttributes, so we scan the keys ourselves
// rather than depending on getAttribute's own case-folding -- it reaches
// case-insensitivity by a different mechanism than the attribute map, exactly the
// kind of internal a repackaging release can drift on. No `|| {}` fallback: an
// element node always exposes rawAttributes, so if that ever stops being true we
// want a loud throw in CI, not a silent `false` that reopens the vanishing-embed hole.
const hasPayloadAttribute = (el) =>
  Object.entries(el.rawAttributes).some(
    ([name, value]) => PAYLOAD_ATTRIBUTES.includes(name.toLowerCase()) && Boolean(value)
  );

// "Content-bearing" means the element carried a payload (a resource attribute,
// real text, or child elements) rather than being an empty stub. Whitespace-only
// text does not count. Drives whether we notify the user.
const hasContent = (el) => hasPayloadAttribute(el) || el.text.trim().length > 0 || el.querySelectorAll('*').length > 0;

export const stripDisallowedTags = (html, tags = HTML_DISALLOWED_TAGS) => {
  if (!html) {
    return { html, removed: 0, removedWithContent: 0, root: null };
  }

  // Fast path: skip parsing entirely when no candidate tag is present.
  const lower = html.toLowerCase();
  if (!tags.some((tag) => lower.includes('<' + tag))) {
    return { html, removed: 0, removedWithContent: 0, root: null };
  }

  // comment: true keeps this helper surgical — it removes only the disallowed
  // tags and preserves everything else, including Outlook `<!--[if mso]>`
  // conditional comments. The parsed `root` is returned so callers can reuse it
  // (e.g. to query product blocks) instead of re-parsing the serialized string,
  // which would otherwise drop those comments again.
  const root = parse(html, { comment: true });
  let removed = 0;
  let removedWithContent = 0;

  for (const tag of tags) {
    for (const el of root.querySelectorAll(tag)) {
      if (hasContent(el)) {
        removedWithContent += 1;
      }
      el.remove();
      removed += 1;
    }
  }

  return { html: root.toString(), removed, removedWithContent, root };
};
